import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { thermostatComfortSettingsCollection } from '@/reducers/thermostatComfortSettings'
import { inactiveComfortSettingsCollection } from '@/reducers/inactiveComfortSettings'
import { useAppDispatch } from '@/request/utils'
import { thermostatsCollection } from '@/reducers/thermostats'
import { useNavigation } from '@/app/hooks'

export default function useInactiveTimeSchedulesViewModel() {
  const { thermostatId: thermostatIdParam } = useParams<{
    thermostatId: string
  }>()
  const navigation = useNavigation()
  const thermostatId = Number.parseInt(thermostatIdParam)

  const dispatch = useAppDispatch()

  // Comfort settings list for the thermostat
  const { data: comfortSettings, isLoading: comfortSettingsLoading } =
    thermostatComfortSettingsCollection.useFetch(
      `/thermostats/${thermostatId}/comfort_settings/`,
    )

  const { refetch: refetchThermostats } = thermostatsCollection.useFetch()

  const firstComfortSettingId = comfortSettings[0]?.id

  // Fetch inactive comfort settings; endpoint requires a comfort_setting_id in the path,
  // but GET returns the inactive settings for the whole thermostat.
  const inactiveFetch = inactiveComfortSettingsCollection.useFetch(
    `/thermostats/${thermostatId}/comfort_settings/${
      firstComfortSettingId ?? ''
    }/inactive/`,
    { require: Boolean(firstComfortSettingId) },
  )

  const inactiveSettings = inactiveFetch.data

  const originalInactiveComfortSettingIds = useMemo(() => {
    const ids = new Set<number>()
    for (const item of inactiveSettings) {
      const csId = item?.comfort_setting?.id
      if (typeof csId === 'number') ids.add(csId)
    }
    return ids
  }, [inactiveSettings])

  // Local selection state mirrors the server list until the user changes it
  const [selectedComfortSettingIds, setSelectedComfortSettingIds] = useState<
    Set<number>
  >(new Set())

  // State for confirmation modal
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)
  const [pendingNavigation, setPendingNavigation] = useState<
    (() => void) | null
  >(null)

  // Initialize selection when data loads or changes
  useEffect(() => {
    setSelectedComfortSettingIds(new Set(originalInactiveComfortSettingIds))
  }, [
    Array.from(originalInactiveComfortSettingIds)
      .sort((a, b) => a - b)
      .join(','),
  ])

  const onToggle = (comfortSettingId: number, checked: boolean) => {
    setSelectedComfortSettingIds((prev) => {
      const next = new Set(prev)
      if (checked) {
        next.add(comfortSettingId)
      } else {
        next.delete(comfortSettingId)
      }
      return next
    })
  }

  // Compute if there are unsaved changes
  const hasChanges = useMemo(() => {
    if (
      selectedComfortSettingIds.size !== originalInactiveComfortSettingIds.size
    ) {
      return true
    }
    for (const id of selectedComfortSettingIds) {
      if (!originalInactiveComfortSettingIds.has(id)) return true
    }
    return false
  }, [selectedComfortSettingIds, originalInactiveComfortSettingIds])

  const onSave = async () => {
    // Diff
    const toCreate: number[] = []
    const toDelete: number[] = []
    for (const id of selectedComfortSettingIds) {
      if (!originalInactiveComfortSettingIds.has(id)) toCreate.push(id)
    }
    for (const id of originalInactiveComfortSettingIds) {
      if (!selectedComfortSettingIds.has(id)) toDelete.push(id)
    }

    // Build lookup from comfort setting id -> inactive record id
    const byComfortSettingId = new Map<number, number | string>()
    for (const item of inactiveSettings) {
      const csId = item?.comfort_setting?.id
      if (typeof csId === 'number') byComfortSettingId.set(csId, item.id)
    }

    // Fire requests
    const creations = toCreate.map((csId) =>
      dispatch(
        inactiveComfortSettingsCollection.actions.create(
          {},
          {
            pathSegments: [
              'thermostats',
              thermostatId.toString(),
              'comfort_settings',
              csId.toString(),
              'inactive',
            ],
          },
        ),
      ),
    )
    const deletions = toDelete.map((csId) =>
      dispatch(
        inactiveComfortSettingsCollection.actions.remove('inactive', {
          id: byComfortSettingId.get(csId),
          pathSegments: [
            'thermostats',
            thermostatId.toString(),
            'comfort_settings',
            csId.toString(),
          ],
        }),
      ),
    )

    await Promise.all([...creations, ...deletions])
    await inactiveFetch.refetch()
    await refetchThermostats()
    // Go back to the L2 page
    navigation.goBack()
  }

  // Confirmation modal handlers
  const onConfirmNavigation = () => {
    setShowConfirmationModal(false)
    if (pendingNavigation) {
      pendingNavigation()
      setPendingNavigation(null)
    }
  }

  const onCancelNavigation = () => {
    setShowConfirmationModal(false)
    setPendingNavigation(null)
  }

  const onDiscardAndNavigate = () => {
    // For schedule view, just navigate without saving (no empty periods to clean up)
    setShowConfirmationModal(false)
    if (pendingNavigation) {
      pendingNavigation()
      setPendingNavigation(null)
    }
  }

  const onSaveAndNavigate = async () => {
    await onSave()
    setShowConfirmationModal(false)
    if (pendingNavigation) {
      pendingNavigation()
      setPendingNavigation(null)
    }
  }

  const onNavigationAttempt = (navigationCallback: () => void) => {
    if (hasChanges) {
      setPendingNavigation(() => navigationCallback)
      setShowConfirmationModal(true)
    } else {
      navigationCallback()
    }
  }

  return {
    thermostatId,
    comfortSettings,
    inactiveComfortSettingIds: selectedComfortSettingIds,
    isLoading: comfortSettingsLoading || inactiveFetch.isLoading,
    hasChanges,
    showConfirmationModal,
    onToggle,
    onSave,
    onNavigationAttempt,
    onConfirmNavigation,
    onCancelNavigation,
    onDiscardAndNavigate,
    onSaveAndNavigate,
  }
}
